import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { injectIntl } from 'react-intl';
import {
  PieChart, Pie, ResponsiveContainer, Cell, Legend, Tooltip,
} from 'recharts';
import {
  sum, values, isEmpty, filter, map,
} from 'lodash/fp';
import { PIE_COLORS } from '../../common/constants';
import messages from '../messages';
import QuestionTitle from './QuestionTitle';
import QuestionInfo from './QuestionInfo';
import OtherAnswers from './OtherAnswers';
import ChartTooltipContent from './ChartTooltipContent';

const RADIAN = Math.PI / 180;

const S = {
  QuestionInfo: styled(QuestionInfo)`
    margin-bottom: 24px;
  `,
  ResponsiveContainer: styled(ResponsiveContainer)`
    margin-left: 8px;
  `,
};

const SingleSelectionQuestion = ({
  intl, question, stats, onUpdate,
}) => {
  const isOthers = key => key === 'others';
  const isOthersOption = type => type === 'TEXT';

  const hasOtherAnswer = !isEmpty(filter(option => isOthersOption(option.type), question.options));

  const answerCount = useMemo(() => sum(map.convert({ cap: false })((value, key) => (
    (isOthers(key) ? sum(values(value)) : value)
  ), stats)), [stats]);

  const answerData = useMemo(() => (map((option) => {
    const data = {};

    if (isOthersOption(option.type)) {
      data.name = '기타';
      data.value = isEmpty(stats.others) ? 0 : sum(values(stats.others));
    } else {
      data.name = `${option.number}번`;
      data.value = stats[option.number] || 0;
    }

    return data;
  }, question.options)), [question, stats]);

  const renderCustomizedLabel = useCallback(({
    cx, cy, midAngle, innerRadius, outerRadius, percent, value,
  }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (value <= 0
      ? null : (
        <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
          {`${(percent * 100).toFixed(0)}%`}
        </text>
      )
    );
  }, []);

  return (
    <div>
      <QuestionTitle number={question.number} onUpdate={onUpdate}>
        {question.content
          || intl.formatMessage(
            messages.singleSelectionQuestionNoContent,
            { number: question.number },
          )}
      </QuestionTitle>
      <S.QuestionInfo>
        {`단일 선택형 / ${question.required ? '필수응답' : '선택응답'} / 응답 ${answerCount}명`}
      </S.QuestionInfo>
      <S.ResponsiveContainer ratio={1} height={300}>
        <PieChart>
          <Pie
            data={answerData}
            labelLine={false}
            label={renderCustomizedLabel}
            dataKey="value"
            stroke="none"
          >
            {map.convert({ cap: false })((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={PIE_COLORS[index % PIE_COLORS.length]}
              />
            ), answerData)}
          </Pie>
          <Legend
            layout="vertical"
            verticalAlign="top"
            align="right"
          />
          <Tooltip content={<ChartTooltipContent percentParameter={answerCount} />} />
        </PieChart>
      </S.ResponsiveContainer>
      {hasOtherAnswer
        ? (
          <OtherAnswers
            stats={stats.others}
          />
        ) : null}
    </div>
  );
};

SingleSelectionQuestion.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  question: PropTypes.shape({
    number: PropTypes.number.isRequired,
    type: PropTypes.oneOf([
      'SINGLE_SELECTION',
      'MULTIPLE_SELECTION',
      'DESCRIPTIVE',
      // XXX: 하위 호환성 유지를 위한 필드. 추후 제거 예정
      'SINGLE_CHOICE',
      'MULTIPLE_CHOICE',
      'SUBJECTIVE',
    ]).isRequired,
    content: PropTypes.string.isRequired,
    position: PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
    }),
    size: PropTypes.shape({
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
    }),
    required: PropTypes.bool.isRequired,
    field: PropTypes.shape({
      width: PropTypes.number,
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
    }),
    options: PropTypes.arrayOf(PropTypes.shape({
      content: PropTypes.string.isReuqired,
      number: PropTypes.number.isRequired,
      position: PropTypes.shape({
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
      }),
    })),
  }).isRequired,
  stats: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.number, PropTypes.objectOf(PropTypes.number),
    ]),
  ),
  onUpdate: PropTypes.func.isRequired,
};

SingleSelectionQuestion.defaultProps = {
  stats: null,
};

export default injectIntl(SingleSelectionQuestion);
